import { z } from 'zod';
import { alertSchema, computedMetricsSchema, dashboardUserSchema, funnelStageSchema, metaRowSchema, metricDefSchema, ruleSchema, segmentClassificationSchema, targetsMapSchema, } from './domain.js';
import { count, isoDate, nokAmount, ratio, segmentEnum } from './primitives.js';
const ok = z.literal(true);
// ---- shared request inputs (lenient: handlers tolerate junk + default) -----
export const rangeQuery = z.object({
    from: isoDate.optional().catch(undefined),
    to: isoDate.optional().catch(undefined),
    segment: segmentEnum.optional().catch(undefined),
});
export const adsQuery = rangeQuery.extend({
    level: z.enum(['ad', 'campaign']).optional().catch(undefined),
});
export const planRatesQuery = z.object({
    lookback: z.coerce.number().int().optional().catch(undefined),
});
// ---- GET /config (public) --------------------------------------------------
export const configResponse = z.object({
    googleClientId: z.string().nullable(),
    brand: z.string(),
    tz: z.string(),
    currency: z.literal('NOK'),
    configured: z.boolean(),
});
// ---- GET /me ---------------------------------------------------------------
export const meResponse = z.object({ ok, user: dashboardUserSchema });
// ---- POST/DELETE /session — cookie session (sign in once, stay in) ----------
export const sessionResponse = z.object({ ok, user: dashboardUserSchema });
export const sessionDeleteResponse = z.object({ ok });
// ---- GET /freshness — when daily_metrics was last (re)computed -------------
export const freshnessResponse = z.object({ ok, refreshedAt: z.string().nullable() });
// ---- GET /metrics — discriminated union on `segment` -----------------------
export const metricsAllResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    segment: z.literal('all'),
    defs: z.array(metricDefSchema),
    targets: targetsMapSchema,
    rows: z.array(computedMetricsSchema),
    totals: computedMetricsSchema.nullable(),
    segmentNote: z.null(),
});
export const metricsSegResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    segment: z.enum(['lead', 'booking']),
    defs: z.array(metricDefSchema),
    targets: targetsMapSchema,
    rows: z.array(metaRowSchema),
    totals: metaRowSchema.nullable(),
    segmentNote: z.string(),
    classification: segmentClassificationSchema,
});
export const metricsResponse = z.discriminatedUnion('segment', [
    metricsAllResponse,
    metricsSegResponse,
]);
// ---- GET /funnel — plain union (the `all` branch emits no `segment`) --------
export const funnelAllResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    totals: z.object({
        spend: nokAmount,
        impressions: count,
        reach: count,
        clicks: count,
        leads: count,
        bookings: count,
        showedUp: count,
        sold: count,
        revenue: nokAmount,
        cash: nokAmount,
    }),
    stages: z.array(funnelStageSchema),
    derived: z.object({ cpa: ratio, rRoi: ratio, cRoi: ratio, rPl: nokAmount, cPl: nokAmount }),
});
export const funnelSegResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    segment: z.enum(['lead', 'booking']),
    stages: z.array(funnelStageSchema),
    totals: z.object({
        spend: nokAmount,
        impressions: count,
        reach: count,
        clicks: count,
        leads: count,
        bookings: count,
    }),
    derived: z.object({ costPerBooking: ratio.optional(), cpl: ratio.optional() }),
    segmentNote: z.string(),
});
export const funnelResponse = z.union([funnelSegResponse, funnelAllResponse]);
// ---- GET /ads --------------------------------------------------------------
/** Creative-engagement diagnostics per ad (derived from ad-level Meta insights).
 * All rates are 0–100 % or ratios, null when no denominator. Video fields are
 * null/0 for image ads (isVideo=false). Benchmarks live on the client. */
export const adEngagementSchema = z.object({
    isVideo: z.boolean(),
    hookRate: ratio, // 3s plays ÷ impressions
    holdRate: ratio, // thruplays ÷ 3s plays
    thruplayRate: ratio, // thruplays ÷ impressions
    completionPct: ratio, // p100 ÷ impressions
    avgWatchSec: ratio, // impression-weighted avg seconds watched
    engagementRate: ratio, // post engagements ÷ impressions
    linkCtr: ratio, // link clicks ÷ impressions
    outboundCtr: ratio, // outbound clicks ÷ impressions
    frequency: ratio, // impressions ÷ reach (approx over multi-day ranges)
    costPerThruplay: ratio, // NOK
    costPer3s: ratio, // NOK
    retention: z.object({ p25: ratio, p50: ratio, p75: ratio, p100: ratio }),
});
/** Per-ad keep/kill/scale verdict from the server-side decision tree
 * (apps/api/src/dashboard/recommend.ts). Reasons are CODES + numeric params —
 * the client renders localized sentences (nb/en templates live client-side). */
export const adVerdictEnum = z.enum(['scale', 'keep', 'iterate', 'watch', 'kill', 'inactive']);
export const adRecommendationSchema = z.object({
    verdict: adVerdictEnum,
    confidence: z.enum(['high', 'medium', 'low']),
    reasons: z.array(z.object({
        code: z.string(),
        params: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
    })),
});
export const adRowSchema = z.object({
    entityId: z.string(),
    name: z.string(),
    // Campaign + ad-set identity (Meta), for the campaign → ad set → ad tree in
    // the Overview drill-down. Null until backfilled / for campaign-level rows.
    campaignId: z.string().nullable(),
    campaignName: z.string().nullable(),
    adsetId: z.string().nullable(),
    adsetName: z.string().nullable(),
    spend: nokAmount,
    impressions: count,
    clicks: count,
    leads: count,
    schedule: count,
    bookings: count,
    ctr: ratio,
    cpc: ratio,
    cpl: ratio,
    cpBooking: ratio,
    // Per-ad membership revenue attributed via the member→ad join (same basis as
    // the bookings drilldown). revenue = revenueNok − refundsNok; rRoi = revenue ÷
    // spend. First-month / period-blended, inherits the attribution caveats.
    revenueNok: nokAmount, // gross membership revenue attributed to this entity (kr)
    refundsNok: nokAmount, // refunds attributed (kr)
    revenue: nokAmount, // net membership revenue (kr)
    rRoi: ratio, // revenue ÷ spend (null when no spend)
    engagement: adEngagementSchema,
    recommendation: adRecommendationSchema,
});
export const adsResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    level: z.enum(['ad', 'campaign']),
    ads: z.array(adRowSchema),
    scheduleTotal: count,
    // Revenue coverage for the period (kr, net of refunds): how much membership
    // revenue could be tied to an ad at all, and how much of that is session-proven.
    revenueTotal: nokAmount,
    revenueAttributed: nokAmount,
    sessionAttributed: nokAmount,
});
// ---- GET /bookings ---------------------------------------------------------
// Named bookings from Momence sales (appointment items), enriched with our
// attribution pairing (heuristic — Meta's per-ad counts are Meta's own) and
// an internal-tester flag so staff test bookings are visible at a glance.
export const bookingEntrySchema = z.object({
    saleId: z.number(),
    bookedAt: z.string(), // ISO timestamp from Momence saleDate
    date: isoDate, // Oslo day bucket (matches daily_metrics)
    name: z.string(),
    memberId: z.number().nullable(),
    internal: z.boolean(),
    ad: z
        .object({
        adId: z.string().nullable(),
        adName: z.string().nullable(),
        campaign: z.string().nullable(),
        // 'session' = paired via the lp_booked beacon fired in the booker's own
        // session (session-proven); 'heuristic' = the visit-pairing fallback.
        method: z.enum(['session', 'heuristic']).nullable(),
    })
        .nullable(),
});
export const bookingsResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    bookings: z.array(bookingEntrySchema), // newest first
    counts: z.object({
        total: count,
        internal: count,
        adMatched: count,
    }),
});
// ---- GET /plan-rates -------------------------------------------------------
const planRatesObj = z.record(z.string(), ratio).nullable();
export const planRatesResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    lookback: z.number(),
    days: z.number(),
    totals: z.record(z.string(), z.number()).nullable(),
    metrics: computedMetricsSchema.nullable(),
    rates: planRatesObj,
    denominators: z.record(z.string(), z.number()).nullable(),
    surArtifact: z.boolean(),
    ads: z.record(z.string(), z.number()).nullable(),
    benchmarks: z.record(z.string(), z.number()),
    constants: z.record(z.string(), z.number()),
    caveats: z.array(z.string()),
});
// ---- /targets --------------------------------------------------------------
export const targetsResponse = z.object({
    ok,
    targets: targetsMapSchema,
    defs: z.array(metricDefSchema),
});
export const putTargetsBody = z.object({
    metricKey: z.string(),
    comparator: z.enum(['gte', 'lte']),
    targetValue: z.number(),
    notes: z.string().nullish(),
});
export const putTargetsResponse = z.object({ ok, targets: targetsMapSchema });
// ---- /rules ----------------------------------------------------------------
export const rulesResponse = z.object({ ok, rules: z.array(ruleSchema) });
export const patchRulesParams = z.object({ id: z.coerce.number().int() });
export const patchRulesBody = z.object({
    enabled: z.boolean().optional(),
    threshold: z.number().optional(),
    windowDays: z.number().optional(),
});
// ---- /alerts ---------------------------------------------------------------
export const alertsResponse = z.object({ ok, alerts: z.array(alertSchema) });
// ---- POST /refresh ---------------------------------------------------------
export const refreshBody = z.object({
    from: isoDate.optional().catch(undefined),
    to: isoDate.optional().catch(undefined),
});
export const refreshResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    result: z.object({ days: z.number(), note: z.string() }),
});
// ---- error envelope --------------------------------------------------------
export const errorResponse = z.object({ ok: z.literal(false), error: z.string() });
//# sourceMappingURL=endpoints.js.map